import type { InventoryResource } from '@repo/api-gw-sdk';
import { BackupStatus, QuotaStatus, ResourceType } from '@repo/api-gw-sdk';
import { useRouter } from 'next/navigation';

import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import {
  InventoryResourceActionsKeys,
  type ResourceAction,
} from '@/data/inventory/actions';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import { isDateValid } from '@/utils/dateTime';

const useInstanceActions = (
  resource: InventoryResource | undefined,
  onEntityChange: () => Promise<void>,
  setAssignBackupPolicyDialogOpen: (isOpen: boolean) => void,
  setExcludeFromBackupDialogOpen: (isOpen: boolean) => void,
  setTakeSnapshotDialogOpen: (isOpen: boolean) => void,
  setActionRequiredDialogOpen: (isOpen: boolean) => void
) => {
  const dal = useDAL();
  const { isAuthorizedResource } = useRoles();
  const router = useRouter();

  const triggerTakeSnapshot = () => {
    setTakeSnapshotDialogOpen(true);
  };

  const {
    data: limits,
    isLoading: isLimitsLoading,
    mutate: mutateLimits,
  } = dal.inventory.limits.useLimit();

  const takeSnapshotAction: ResourceAction = {
    id: 'take-snapshot-action',
    key: InventoryResourceActionsKeys.TakeSnapshot,
    tooltip: 'Take on-demand snapshot',
    icon: 'material-symbols-backup-outline-rounded',
    isAuthorized: isAuthorizedResource('create:jobs', resource),
    disabled: () =>
      !takeSnapshotAction.isAuthorized ||
      resource?.backupStatus === BackupStatus.Terminated ||
      resource?.backupStatus === BackupStatus.Disconnected ||
      resource?.backupStatus === BackupStatus.Unsupported ||
      resource?.backupStatus === BackupStatus.LimitExceeded,
    execute: () => {
      triggerTakeSnapshot();
    },
    predicate: () =>
      resource?.resourceType === ResourceType.AwsEc2 ||
      resource?.resourceType === ResourceType.AwsEbsVolume ||
      resource?.resourceType === ResourceType.AwsRds ||
      resource?.resourceType === ResourceType.AwsS3 ||
      resource?.resourceType === ResourceType.AwsEksNamespace ||
      resource?.resourceType === ResourceType.AwsEfs ||
      resource?.resourceType === ResourceType.AwsFsx ||
      resource?.resourceType === ResourceType.AtlasMongodbCluster ||
      resource?.resourceType === ResourceType.AzureStorageAccount ||
      resource?.resourceType === ResourceType.AzureVirtualMachine ||
      resource?.resourceType === ResourceType.AzureSqlVirtualMachine ||
      resource?.resourceType === ResourceType.AzureSapHanaVm ||
      resource?.resourceType === ResourceType.AzurePostgresql ||
      resource?.resourceType === ResourceType.AzureSqlDatabase ||
      resource?.resourceType === ResourceType.GcpComputeEngineInstance ||
      resource?.resourceType === ResourceType.GcpBigQuery ||
      resource?.resourceType === ResourceType.GcpCloudSqlInstance ||
      resource?.resourceType === ResourceType.GcpCloudStorageBucket ||
      resource?.resourceType === ResourceType.GcpCloudFirestore ||
      resource?.resourceType === ResourceType.GcpGkeNamespace ||
      resource?.resourceType === ResourceType.AwsDynamoDb ||
      resource?.resourceType === ResourceType.AwsKeyspacesTable ||
      resource?.resourceType === ResourceType.AzureAksNamespace ||
      resource?.resourceType === ResourceType.GcpDisk,
  };

  const triggerAllowBackups = async (resource: InventoryResource) => {
    await dal.inventory.resources
      .cancelResourceBackupExclusion(resource.id)
      .then(() => {
        void onEntityChange();
        void mutateLimits();
      });
  };

  const allowBackupsAction: ResourceAction = {
    id: 'allow-backups-action',
    key: InventoryResourceActionsKeys.AllowBackups,
    isAuthorized: isAuthorizedResource('update:exclude_resource', resource),
    disabled: () => !allowBackupsAction.isAuthorized,
    title: 'Cancel Backup Exclusion',
    tooltip: 'Cancel backup exclusion',
    icon: 'material-symbols-add-circle-outline',
    execute: (resource: InventoryResource) => {
      void triggerAllowBackups(resource);
    },
    predicate: () => resource?.backupStatus === BackupStatus.ExcludedFromBackup,
  };

  const excludeFromBackupAction: ResourceAction = {
    id: 'exclude-from-backup-action',
    key: InventoryResourceActionsKeys.ExcludeFromBackup,
    isAuthorized: isAuthorizedResource('update:exclude_resource', resource),
    disabled: () => !excludeFromBackupAction.isAuthorized,
    title: 'Exclude from Backup',
    tooltip: 'Exclude from backup',
    icon: 'material-symbols-hide-source-outline-rounded',
    execute: () => {
      setExcludeFromBackupDialogOpen(true);
    },
    predicate: () => resource?.backupStatus !== BackupStatus.ExcludedFromBackup,
  };

  const triggerExploreDB = (resource: InventoryResource) => {
    router.push(`/db/${resource.id}`);
  };

  const exploreDBAction: ResourceAction = {
    id: 'explore-db-action',
    key: InventoryResourceActionsKeys.Explore,
    title: 'Explore Database',
    tooltip:
      isDateValid(resource?.latestSnapshotTime) ||
      isDateValid(resource?.clusterLatestSnapshotTime)
        ? 'Explore database'
        : `This resource doesn't have any Eon snapshots and can't be explored.`,
    icon: 'material-symbols-frame-inspect-rounded',
    isAuthorized: isAuthorizedResource('read:db', resource),
    disabled: () =>
      !exploreDBAction.isAuthorized ||
      (!isDateValid(resource?.latestSnapshotTime) &&
        !isDateValid(resource?.clusterLatestSnapshotTime)),
    execute: (resource: InventoryResource) => {
      triggerExploreDB(resource);
    },
    predicate: () =>
      resource?.resourceType === ResourceType.AwsRds ||
      resource?.resourceType === ResourceType.AwsDynamoDb ||
      resource?.resourceType === ResourceType.AwsKeyspacesTable ||
      resource?.resourceType === ResourceType.AtlasMongodbCluster ||
      resource?.resourceType === ResourceType.AzureCosmosdbNosql ||
      resource?.resourceType === ResourceType.AzureSqlManagedInstance ||
      resource?.resourceType === ResourceType.AzurePostgresql ||
      resource?.resourceType === ResourceType.AzureSqlVirtualMachine ||
      resource?.resourceType === ResourceType.AzureMysql ||
      resource?.resourceType === ResourceType.AzureSqlDatabase ||
      resource?.resourceType === ResourceType.GcpCloudSqlInstance ||
      resource?.resourceType === ResourceType.GcpCloudFirestore ||
      resource?.resourceType === ResourceType.GcpBigQuery ||
      ((resource?.resourceType === ResourceType.AwsEc2 ||
        resource?.resourceType === ResourceType.AzureVirtualMachine ||
        resource?.resourceType === ResourceType.GcpComputeEngineInstance ||
        resource?.resourceType === ResourceType.AwsEksNamespace) &&
        !!resource?.classifications?.appsDetails?.apps?.find(
          (app) => app.paths?.length
        )),
  };

  const triggerExploreFiles = (resource: InventoryResource) => {
    router.push(`/explorer/${resource.id}`);
  };

  const exploreFilesAction: ResourceAction = {
    id: 'explore-files-action',
    key: InventoryResourceActionsKeys.Explore,
    title: 'Explore Files',
    tooltip:
      isDateValid(resource?.latestSnapshotTime) ||
      isDateValid(resource?.clusterLatestSnapshotTime)
        ? 'Explore files'
        : `This resource doesn't have any Eon snapshots and can't be explored.`,
    icon: 'material-symbols-frame-inspect-rounded',
    isAuthorized: isAuthorizedResource('read:files', resource),
    disabled: () =>
      !exploreFilesAction.isAuthorized ||
      (!isDateValid(resource?.latestSnapshotTime) &&
        !isDateValid(resource?.clusterLatestSnapshotTime)),
    execute: (resource: InventoryResource) => {
      triggerExploreFiles(resource);
    },
    predicate: () =>
      resource?.resourceType === ResourceType.AwsEc2 ||
      resource?.resourceType === ResourceType.AwsS3 ||
      resource?.resourceType === ResourceType.AwsEksNamespace ||
      resource?.resourceType === ResourceType.AzureStorageAccount ||
      resource?.resourceType === ResourceType.AzureVirtualMachine ||
      resource?.resourceType === ResourceType.AzureFileShare ||
      resource?.resourceType === ResourceType.GcpComputeEngineInstance ||
      resource?.resourceType === ResourceType.GcpCloudStorageBucket ||
      resource?.resourceType === ResourceType.GcpGkeNamespace ||
      resource?.resourceType === ResourceType.AzureAksNamespace,
  };

  const assignToBackupPolicyAction: ResourceAction = {
    id: 'assign-to-backup-policy-action',
    key: InventoryResourceActionsKeys.AssignToBackupPolicy,
    title: 'Assign to Backup Policy',
    tooltip: 'Assign to backup policy',
    icon: 'material-symbols-bookmark-add-outline',
    isAuthorized: isAuthorizedResource('update:policies', resource),
    disabled: () => !assignToBackupPolicyAction.isAuthorized,
    execute: () => {
      setAssignBackupPolicyDialogOpen(true);
    },
  };

  const showActionRequiredAction: ResourceAction = {
    id: 'show-action-required-action',
    key: InventoryResourceActionsKeys.ShowActionRequired,
    title: 'Complete Configuration',
    tooltip: 'Complete configuration',
    icon: 'material-symbols-settings-ethernet',
    execute: () => {
      setActionRequiredDialogOpen(true);
    },
    predicate: () =>
      resource?.backupStatus === BackupStatus.ActionRequired &&
      (resource?.resourceType === ResourceType.AzureSqlVirtualMachine ||
        resource?.resourceType === ResourceType.AzureSapHanaVm ||
        resource?.resourceType === ResourceType.GcpBigQuery),
  };

  const enableResourceFromLimitExceededAction: ResourceAction = {
    id: 'enable-resource-from-limit-exceeded-action',
    key: InventoryResourceActionsKeys.EnableResourceFromLimitExceeded,
    title: 'Enable Resource',
    tooltip: 'Enable resource',
    icon: '', // Only used in menu, not in the actions bar
    isAuthorized: isAuthorizedResource('update:exclude_resource', resource),
    execute: (resource: InventoryResource) => {
      void dal.inventory.resources
        .enableResourceFromLimitExceeded(resource.id)
        .then(() => {
          void onEntityChange();
          void mutateLimits();
        });
    },
    disabled: () => {
      const resourceType = resource?.resourceType;

      if (
        !enableResourceFromLimitExceededAction.isAuthorized ||
        (isLimitsLoading && limits === undefined) ||
        resourceType === undefined
      ) {
        return true;
      }
      if (limits === undefined) {
        return false;
      }

      const currUsage = limits?.[resourceType]?.currentQuotaUsage;
      const quotaLimit = limits?.[resourceType]?.quotaLimit;

      if (quotaLimit === undefined || quotaLimit === null) return false;

      if (currUsage === undefined || currUsage === null) return true;

      return currUsage >= quotaLimit;
    },
    disabledTooltip: resource?.resourceType
      ? `You have ${limits?.[resource.resourceType]?.currentQuotaUsage}/${limits?.[resource.resourceType]?.quotaLimit} ${ResourceTypes[resource.resourceType]?.displayName}s in usage.\nPlease exclude another resource in order to enable this`
      : undefined,
    predicate: () =>
      resource?.quotaStatus === QuotaStatus.MaxResourcesExceeded &&
      !!resource?.resourceType,
  };

  const actions: ResourceAction[] = [
    assignToBackupPolicyAction,
    exploreFilesAction,
    exploreDBAction,
    takeSnapshotAction,
    excludeFromBackupAction,
    allowBackupsAction,
    showActionRequiredAction,
    enableResourceFromLimitExceededAction,
  ].filter((action) => (action.predicate ? action.predicate() : true));

  return actions;
};

export { useInstanceActions };
